import { allNewArticles as blogArticles } from "@/data/blogArticles";

/**
 * Single source of truth for the education hubs: navigation, hub pages,
 * breadcrumbs and sitemap all read from here.
 */
export interface HubLink {
  href: string;
  title: string;
  note?: string;
}

export interface EducationHub {
  slug: string;
  /** Spanish counterpart path (/es/...), when one exists. */
  spanishPath?: string;
  /** Public path of the hub page. */
  path: string;
  title: string;
  short: string;
  intro: string;
  metaTitle: string;
  metaDescription: string;
  links: HubLink[];
}

type Article = { slug: string; category: string; title: string; subtitle: string };
const all = blogArticles as unknown as Article[];

const fromArticles = (test: (a: Article) => boolean): HubLink[] =>
  all.filter(test).map((a) => ({ href: `/blog/${a.slug}`, title: a.title, note: a.subtitle }));

const has = (...words: string[]) => (a: Article) => words.some((w) => a.slug.includes(w));
const cat = (...cats: string[]) => (a: Article) => cats.includes(a.category);

const dedupe = (links: HubLink[]) => {
  const seen = new Set<string>();
  return links.filter((l) => (seen.has(l.href) ? false : (seen.add(l.href), true)));
};

export const EDUCATION_HUBS: EducationHub[] = [
  {
    slug: "dump-trailers",
    path: "/trailer-guides/dump-trailers",
    title: "Dump Trailer Education",
    short: "Sizes, hoists, wall heights, weight classes and roll-off basics.",
    intro:
      "Everything that matters about dump and roll-off trailers: how the hoists work, which weight class fits the material you move, and the mistakes owners make in year one.",
    metaTitle: "Dump Trailer Guides: Sizes, Hoists & Weight Classes | Juan The Trailer Guy",
    metaDescription:
      "Independent dump trailer education: 14K vs 16K, telescopic vs scissor hoists, sizes, wall heights and roll-off basics explained in plain language.",
    links: dedupe([
      { href: "/dump-trailers", title: "Dump Trailers Explained", note: "Configurations, sizes and who uses them." },
      { href: "/dump-trailer-buyers-guide", title: "Dump Trailer Buyer's Guide", note: "What to check before you buy any dump trailer." },
      { href: "/blog/14k-vs-16k-dump-trailer", title: "14K vs 16K Dump Trailer" },
      { href: "/blog/best-dump-trailer-for-contractors", title: "Best Dump Trailer Setups for Contractors" },
      { href: "/blog/roll-off-vs-dump-trailer", title: "Roll-Off vs Dump Trailer" },
      { href: "/roll-off-trailers", title: "Roll-Off Trailers Explained" },
      { href: "/blog/roll-off-trailers-explained", title: "How Roll-Off Trailers Work" },
      { href: "/blog/roll-off-size", title: "Roll-Off Trailer Sizes" },
      { href: "/blog/roll-off-rail-width", title: "Roll-Off Rail Width" },
      ...fromArticles(cat("Dump Trailers", "Roll-Off Trailers", "Roll Off Trailers")),
    ]),
  },
  {
    slug: "flatbed-equipment",
    path: "/trailer-guides/flatbed-equipment",
    title: "Flatbed & Equipment Trailer Guides",
    short: "Flatbeds, lowboys, tilts and car haulers: what fits your load.",
    intro:
      "Deck lengths, deck widths, ramps vs tilt, gooseneck vs bumper pull. These guides explain how each design handles machines, materials and vehicles.",
    metaTitle: "Flatbed, Equipment & Tilt Trailer Guides | Juan The Trailer Guy",
    metaDescription:
      "Learn how flatbed, lowboy/equipment, gravity tilt, power tilt and car hauler trailers differ, and which design fits what you haul.",
    links: dedupe([
      { href: "/flatbed-trailers", title: "Flatbed Trailers Explained" },
      { href: "/lowboy-trailers", title: "Lowboy / Equipment Trailers Explained" },
      { href: "/tilt-trailers", title: "Tilt Trailers Explained" },
      { href: "/gravity-tilt-trailers", title: "Gravity Tilt Trailers" },
      { href: "/power-tilt-trailers", title: "Power Tilt Trailers" },
      { href: "/car-haulers", title: "Car Haulers Explained" },
      { href: "/blog/lowboy-trailer-guides", title: "Lowboy Trailer Guide Library" },
      { href: "/blog/gravity-tilt-vs-power-tilt", title: "Gravity Tilt vs Power Tilt" },
      { href: "/blog/best-trailer-for-skid-steer", title: "Best Trailer for a Skid Steer" },
      { href: "/blog/best-trailer-for-mini-excavator", title: "Best Trailer for a Mini Excavator" },
      { href: "/blog/how-to-compare-trailers", title: "How to Compare Trailers" },
      ...fromArticles(cat("Flatbed Trailers", "Lowboy Trailers", "Car Haulers", "Power Tilt Trailers", "Gravity Tilt Trailers")),
    ]),
  },
  {
    slug: "towing-cdl",
    spanishPath: "/es/licencia-cdl-remolques",
    path: "/towing-cdl",
    title: "CDL & Non-CDL Towing",
    short: "When you need a CDL, GCWR math and hotshot requirements.",
    intro:
      "The 26,001 lb line, how combined weight is calculated, and what changes when you run commercially. These are plain-language guides to staying legal.",
    metaTitle: "CDL vs Non-CDL Towing Guide: GCWR, Goosenecks & Hotshot | Juan The Trailer Guy",
    metaDescription:
      "When do you need a CDL to tow a trailer? Learn GCWR math, non-CDL gooseneck setups, hotshot requirements and roll-off CDL rules.",
    links: dedupe([
      { href: "/blog/non-cdl-hotshot-trailer-weight-guide", title: "Non-CDL Hotshot Trailer Weight Guide" },
      { href: "/blog/non-cdl-vs-cdl-40-flatbed", title: "Non-CDL vs CDL 40 ft Flatbed" },
      { href: "/blog/roll-off-cdl", title: "Do You Need a CDL for a Roll-Off Trailer?" },
      { href: "/trailer-payload-calculator", title: "Trailer Payload & Towing Calculator" },
      ...fromArticles((a) => has("cdl", "hotshot")(a) || cat("Hotshot Hauling")(a) || has("truck-size", "gooseneck-vs-bumper")(a)),
    ]),
  },
  {
    slug: "gvwr-payload",
    spanishPath: "/es/gvwr-capacidad-de-carga",
    path: "/trailer-guides/gvwr-payload",
    title: "GVWR, Payload & Weight Ratings",
    short: "Read the sticker, do the math, and don't overload.",
    intro:
      "GVWR, payload, axle ratings, tongue weight and derating explained, plus a calculator so you can check your own setup.",
    metaTitle: "GVWR & Payload Explained: Trailer Weight Ratings Guide | Juan The Trailer Guy",
    metaDescription:
      "Understand GVWR, payload, axle ratings, tongue weight and derating. Use the free trailer payload calculator to check your setup.",
    links: dedupe([
      { href: "/trailer-payload-calculator", title: "Trailer Payload Calculator", note: "Check payload, cubic yards and CDL likelihood." },
      { href: "/blog/roll-off-derating", title: "Roll-Off Trailer Derating" },
      { href: "/blog/lowboy-weight-capacity", title: "Lowboy Weight Capacity" },
      { href: "/blog/lowboy-trailer-axle-capacity", title: "Lowboy Axle Capacity" },
      ...fromArticles(has("weight", "gvwr", "payload", "capacity", "axle", "tire-load")),
    ]),
  },
  {
    slug: "maintenance-ownership",
    path: "/maintenance-ownership",
    title: "Trailer Maintenance & Ownership",
    short: "Brakes, bearings, tires, wiring and keeping a trailer working.",
    intro:
      "Practical maintenance: what to check before every trip, what to service every season, and how to recognize parts that are wearing out.",
    metaTitle: "Trailer Maintenance Guides: Brakes, Bearings, Tires & Wiring | Juan The Trailer Guy",
    metaDescription:
      "Trailer maintenance and ownership guides: bearings, brakes, tires, wiring, suspension and couplers explained by someone who's worked with trailers for years.",
    links: dedupe([
      { href: "/blog/trailer-maintenance-tips", title: "Trailer Maintenance Tips" },
      { href: "/blog/235-85r16-14-ply-trailer-tire-guide", title: "235/85R16 14-Ply Trailer Tire Guide" },
      { href: "/blog/mrovi-trailer-parts-northeast-trailer-parts-supplier", title: "Finding Trailer Parts in the Northeast" },
      ...fromArticles(cat("Trailer Parts")),
    ]),
  },
  {
    slug: "financing-education",
    path: "/trailer-guides/financing-education",
    title: "Trailer Financing Education",
    short: "How trailer financing works, without the sales pitch.",
    intro:
      "Commercial vs personal loans, rent-to-own, credit and the questions to ask any lender. This is general education only. We don't take applications or arrange financing.",
    metaTitle: "Trailer Financing Explained: Loans, Credit & Rent-to-Own | Juan The Trailer Guy",
    metaDescription:
      "Independent trailer financing education: how loans work, commercial vs personal, credit scores, rent-to-own, and questions to ask a lender.",
    links: dedupe([
      { href: "/trailer-financing", title: "Trailer Financing Explained" },
      { href: "/trailer-financing-buyers-guide", title: "Trailer Financing Guide" },
      { href: "/blog/rent-to-own-vs-trailer-financing", title: "Rent-to-Own vs Financing" },
      ...fromArticles(cat("Trailer Financing")),
    ]),
  },
  {
    slug: "ownership-costs",
    spanishPath: "/es/costos-de-tener-un-remolque",
    path: "/trailer-guides/ownership-costs",
    title: "Trailer Ownership Costs",
    short: "What a trailer really costs to run: purchase, insurance, upkeep.",
    intro:
      "The sticker price is only the start. These guides cover insurance, startup costs, maintenance budgets and the economics of trailer-based businesses.",
    metaTitle: "Trailer Ownership Costs: Insurance, Upkeep & Startup | Juan The Trailer Guy",
    metaDescription:
      "Understand the real cost of owning a trailer: insurance, maintenance, startup costs and business economics for hotshot, dump and roll-off owners.",
    links: dedupe([
      ...fromArticles(has("cost", "insurance", "income", "finance-vs-cash", "business", "investment", "make-more-money")),
    ]),
  },
  {
    slug: "industry-news",
    path: "/trailer-guides/industry-news",
    title: "Trailer Industry News",
    short: "Industry changes, regulations and trends worth knowing.",
    intro:
      "Notes on regulation changes, material and supply trends, and what's shifting in the trailer world. New posts will appear here as they're published.",
    metaTitle: "Trailer Industry News & Insights | Juan The Trailer Guy",
    metaDescription:
      "Trailer industry news and insights: regulation changes, supply trends and practical takeaways for trailer owners.",
    links: [],
  },
];

export const hubBySlug = (slug: string) => EDUCATION_HUBS.find((h) => h.slug === slug);
